import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  AlertTriangle, 
  Clock, 
  DollarSign,
  FileText,
  Mail,
  Zap,
  RefreshCw,
  Users,
  TrendingUp,
  Eye,
  EyeOff,
  ArrowRight,
  Check,
  X,
  Timer,
  Target,
  ChevronDown,
  Calendar,
  Phone,
  MessageSquare,
  Search,
  Settings,
  Download,
  Upload,
  Send,
  AlertCircle,
  ChevronRight,
  Star,
  MoreHorizontal,
  Archive,
  Trash2,
  Copy,
  FileCheck,
  Shield
} from 'lucide-react';

// EXACT colors from LandingPage.jsx - only blue, black, white
const colors = {
  // Primary accent - deeper blue for a more formal, enterprise feel
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  // Neutral tones
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  
  // Status colors - using only blue/gray variants
  primary: '#1e40af',
  primaryLight: 'rgba(30, 64, 175, 0.1)',
  secondary: '#636363',
  secondaryLight: 'rgba(99, 99, 99, 0.1)',
};

// EXACT font styles from LandingPage.jsx
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600, // Semi-bold for headings
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400, // Regular for body text
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500, // Medium weight
  },
  light: {
    fontWeight: 300, // Light weight
  }
};

// EXACT animation variants from LandingPage.jsx
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  },
};

const fadeInUpVariant = {
  hidden: { y: 40, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1, 
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] } 
  }
};

const ProblemNetworkVisualization = () => {
  const [currentPanel, setCurrentPanel] = useState(0);
  const [animatedCost, setAnimatedCost] = useState(0);
  const [animatedTime, setAnimatedTime] = useState(0);
  
  const [problemRef, problemInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [panelsRef, panelsInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Animated counters
  useEffect(() => {
    if (panelsInView) {
      const interval = setInterval(() => {
        setAnimatedCost(prev => prev < 3700 ? prev + 50 : 3700);
        setAnimatedTime(prev => prev < 26 ? prev + 0.5 : 26);
      }, 50);
      
      return () => clearInterval(interval);
    }
  }, [panelsInView]);

  // Auto-advance panels
  useEffect(() => {
    if (panelsInView) {
      const panelInterval = setInterval(() => {
        setCurrentPanel(prev => (prev + 1) % 4);
      }, 4000);
      
      return () => clearInterval(panelInterval);
    }
  }, [panelsInView]);

  return (
    <div 
      className="pt-8 pb-16 relative overflow-hidden" 
      style={{
        background: 'linear-gradient(to bottom, white, #fafbfd)',
        fontFamily: fontStyles.fontFamily
      }}
    >
      <div className="container mx-auto px-6 max-w-6xl">
        
        {/* Panel 1: ChatGPT Contract Drafting (60% demo, 40% content) */}
        <motion.div
          className="mb-24"
          ref={panelsRef}
          variants={containerVariants}
          initial="hidden"
          animate={panelsInView ? "visible" : "hidden"}
        >
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Demo Side - 60% */}
            <motion.div 
              className="lg:col-span-3 order-1"
              variants={fadeInUpVariant}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.3 }
              }}
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border" 
                   style={{
                     borderColor: 'rgba(226, 232, 240, 0.6)',
                     boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), 0 0 10px rgba(0, 0, 0, 0.02)'
                   }}>
                {/* Browser Header */}
                <div className="h-12 flex items-center px-5 border-b" 
                     style={{
                       background: 'linear-gradient(to right, #f9fafc, #f5f7fa)',
                       borderColor: 'rgba(226, 232, 240, 0.6)'
                     }}>
                  <div className="flex space-x-1.5 mr-5">
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-md px-3 py-1 mx-4">
                    <div className="text-xs" style={{color: colors.gray500}}>🔒 chat.openai.com</div>
                  </div>
                  <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{backgroundColor: colors.accent}}>
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                
                {/* ChatGPT Interface */}
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center mr-3">
                      <div className="text-white text-xs font-bold">O</div>
                    </div>
                    <div className="text-sm font-medium">ChatGPT 4</div>
                    <div className="ml-auto text-xs px-2 py-1 rounded-full" style={{backgroundColor: colors.secondaryLight, color: colors.secondary}}>
                      3.2K tokens used
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium mb-2">You</div>
                      <div className="text-sm">Write a consulting agreement between TechCorp and John Smith for AI development services at $150/hour for 6 months, starting May 15th 2025</div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4 border-l-4" style={{borderColor: colors.accent}}>
                      <div className="text-sm font-medium mb-2 flex items-center">
                        <div className="w-6 h-6 rounded-full bg-gray-200 mr-2"></div>
                        ChatGPT - Attempt #5
                      </div>
                      <div className="text-sm text-gray-700 mb-3">I've created a consulting agreement draft, but please note...</div>
                      <div className="p-3 rounded border" style={{backgroundColor: colors.primaryLight, borderColor: colors.accent}}>
                        <div className="text-xs flex items-center" style={{color: colors.accent}}>
                          <AlertTriangle size={12} className="mr-2" />
                          ⚠️ This AI-generated content may not be legally binding. Please consult with a qualified attorney.
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="text-sm font-medium mb-2">You</div>
                      <div className="text-sm">This is missing liability clauses and indemnification. Can you add those?</div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm font-medium mb-2 flex items-center">
                        <div className="w-6 h-6 rounded-full bg-gray-200 mr-2"></div>
                        ChatGPT
                      </div>
                      <div className="text-sm text-gray-700">I understand you need liability and indemnification clauses. However, these are complex legal provisions that vary by jurisdiction...</div>
                    </div>
                    
                    <div className="border rounded-lg p-3 flex items-center" style={{backgroundColor: colors.secondaryLight, borderColor: colors.secondary}}>
                      <Search size={14} className="mr-2" style={{color: colors.secondary}} />
                      <div className="text-xs" style={{color: colors.secondary}}>Currently googling: "indemnification clause consulting agreement" in 12 other tabs...</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content Side - 40% */}
            <motion.div 
              className="lg:col-span-2 order-2"
              variants={fadeInUpVariant}
            >
              <div className="space-y-8">
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <Zap size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Legal uncertainty</h3>
                    <p className="text-gray-600">AI tools generate contracts but can't guarantee legal compliance or enforceability.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <RefreshCw size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Endless iterations</h3>
                    <p className="text-gray-600">Multiple attempts to get critical clauses right, burning time and tokens.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <Search size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Research rabbit holes</h3>
                    <p className="text-gray-600">Hours spent googling legal terms and clause examples across multiple tabs.</p>
                  </div>
                </motion.div>
              </div>
              
              <div className="mt-8 p-4 rounded-lg" style={{backgroundColor: colors.primaryLight}}>
                <div className="flex items-center">
                  <DollarSign size={20} style={{color: colors.accent}} className="mr-2" />
                  <div>
                    <div className="font-bold" style={{color: colors.accent}}>$550 + 11 hours wasted</div>
                    <div className="text-sm" style={{color: colors.gray600}}>On legal research and failed drafts</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Panel 2: Lawyer Email Chaos (40% content, 60% demo) */}
        <motion.div className="mb-24">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Content Side - 40% */}
            <motion.div 
              className="lg:col-span-2 order-2 lg:order-1"
              variants={fadeInUpVariant}
            >
              <div className="space-y-8">
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <Clock size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Review bottlenecks</h3>
                    <p className="text-gray-600">Lawyers swamped with review requests, creating week-long delays.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Client pressure</h3>
                    <p className="text-gray-600">Prospects asking for updates while legal review drags on indefinitely.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <Calendar size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Schedule chaos</h3>
                    <p className="text-gray-600">Meetings cancelled, reviews postponed, deals stalling in legal limbo.</p>
                  </div>
                </motion.div>
              </div>
              
              <div className="mt-8 p-4 rounded-lg" style={{backgroundColor: colors.secondaryLight}}>
                <div className="flex items-center">
                  <Mail size={20} style={{color: colors.secondary}} className="mr-2" />
                  <div>
                    <div className="font-bold" style={{color: colors.secondary}}>$2,500 + 6 days waiting</div>
                    <div className="text-sm" style={{color: colors.gray600}}>For legal review that's "still in queue"</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Demo Side - 60% */}
            <motion.div 
              className="lg:col-span-3 order-1 lg:order-2"
              variants={fadeInUpVariant}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.3 }
              }}
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border" 
                   style={{
                     borderColor: 'rgba(226, 232, 240, 0.6)',
                     boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), 0 0 10px rgba(0, 0, 0, 0.02)'
                   }}>
                {/* Browser Header */}
                <div className="h-12 flex items-center px-5 border-b" 
                     style={{
                       background: 'linear-gradient(to right, #f9fafc, #f5f7fa)',
                       borderColor: 'rgba(226, 232, 240, 0.6)'
                     }}>
                  <div className="flex space-x-1.5 mr-5">
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-md px-3 py-1 mx-4">
                    <div className="text-xs" style={{color: colors.gray500}}>🔒 mail.google.com</div>
                  </div>
                  <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{backgroundColor: colors.accent}}>
                    <div className="text-white text-xs">47</div>
                  </div>
                </div>
                
                {/* Gmail Interface */}
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{backgroundColor: colors.accent}}>
                      <Mail size={16} color="white" />
                    </div>
                    <div className="text-sm font-medium">Gmail</div>
                    <div className="ml-auto flex items-center space-x-2">
                      <div className="text-xs px-2 py-1 rounded-full" style={{backgroundColor: colors.primaryLight, color: colors.accent}}>47 unread</div>
                      <Settings size={16} className="text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-lg border-l-4" style={{borderColor: colors.accent}}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full mr-3 flex items-center justify-center text-white text-xs font-bold" style={{backgroundColor: colors.accent}}>WA</div>
                          <div>
                            <div className="font-medium text-sm">Wilson & Associates LLP</div>
                            <div className="text-xs text-gray-500">Re: Contract Review - TechCorp Agreement</div>
                          </div>
                        </div>
                        <div className="text-xs font-medium" style={{color: colors.accent}}>6 days ago</div>
                      </div>
                      <div className="text-sm text-gray-700 bg-white p-3 rounded">
                        "Hi there, your contract is still in our review queue. We're currently backlogged with several other matters. Will get to this as soon as possible. Thanks for your patience."
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-100 rounded-lg border-l-4" style={{borderColor: colors.secondary}}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <Calendar size={16} className="mr-3" style={{color: colors.secondary}} />
                          <div>
                            <div className="font-medium text-sm">Legal Review Meeting - CANCELLED</div>
                            <div className="text-xs text-gray-500">Meeting with Wilson & Associates</div>
                          </div>
                        </div>
                        <div className="text-xs font-medium" style={{color: colors.secondary}}>3rd time</div>
                      </div>
                      <div className="text-sm text-gray-700">"Sorry, have to postpone again. Senior partner is tied up with a major case."</div>
                    </div>
                    
                    <div className="p-4 rounded-lg border-l-4" style={{backgroundColor: colors.primaryLight, borderColor: colors.accent}}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full mr-3 flex items-center justify-center text-white text-xs font-bold" style={{backgroundColor: colors.secondary}}>TC</div>
                          <div>
                            <div className="font-medium text-sm">TechCorp Client</div>
                            <div className="text-xs text-gray-500">Contract Status Update?</div>
                          </div>
                        </div>
                        <div className="text-xs font-medium" style={{color: colors.accent}}>2 hours ago</div>
                      </div>
                      <div className="text-sm text-gray-700 bg-white p-3 rounded">
                        "Hi, just checking on the status of our consulting agreement. We're eager to get started and our competitor is also pitching. Any update on timing?"
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gray-100 rounded-lg flex items-center">
                      <AlertTriangle size={16} className="text-gray-500 mr-2" />
                      <div className="text-sm text-gray-600">3 more urgent contract reviews waiting in inbox...</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Panel 3: DocuSign Setup Hell (60% demo, 40% content) */}
        <motion.div className="mb-24">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Demo Side - 60% */}
            <motion.div 
              className="lg:col-span-3 order-1"
              variants={fadeInUpVariant}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.3 }
              }}
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border" 
                   style={{
                     borderColor: 'rgba(226, 232, 240, 0.6)',
                     boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), 0 0 10px rgba(0, 0, 0, 0.02)'
                   }}>
                {/* Browser Header */}
                <div className="h-12 flex items-center px-5 border-b" 
                     style={{
                       background: 'linear-gradient(to right, #f9fafc, #f5f7fa)',
                       borderColor: 'rgba(226, 232, 240, 0.6)'
                     }}>
                  <div className="flex space-x-1.5 mr-5">
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-md px-3 py-1 mx-4">
                    <div className="text-xs" style={{color: colors.gray500}}>🔒 app.docusign.com</div>
                  </div>
                  <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{backgroundColor: colors.secondary}}>
                    <div className="text-white text-xs">!</div>
                  </div>
                </div>
                
                {/* DocuSign Interface */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{backgroundColor: colors.accent}}>
                        <div className="text-white text-xs font-bold">D</div>
                      </div>
                      <div className="text-sm font-medium">DocuSign</div>
                    </div>
                    <div className="text-xs px-2 py-1 rounded-full border" style={{backgroundColor: colors.secondaryLight, color: colors.secondary, borderColor: colors.secondary}}>
                      Free trial: 2 days left
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium">Service_Agreement_v7_FINAL.pdf</div>
                        <div className="flex items-center space-x-2">
                          <div className="text-xs px-2 py-1 rounded" style={{backgroundColor: colors.primaryLight, color: colors.accent}}>Uploaded</div>
                          <MoreHorizontal size={16} className="text-gray-400" />
                        </div>
                      </div>
                      <div className="border rounded p-3" style={{backgroundColor: colors.secondaryLight, borderColor: colors.secondary}}>
                        <div className="text-xs flex items-center" style={{color: colors.secondary}}>
                          <AlertTriangle size={12} className="mr-2" />
                          ⚠️ 12 signature fields need positioning. Watch tutorial first.
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-lg border" style={{backgroundColor: colors.primaryLight, borderColor: colors.accent}}>
                      <div className="flex items-center mb-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center mr-2" style={{backgroundColor: colors.accent}}>
                          <div className="text-white text-xs">?</div>
                        </div>
                        <div className="text-sm font-medium">How to place signature fields</div>
                      </div>
                      <div className="text-xs text-gray-600 mb-2">Tutorial video - 23 minutes remaining</div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full w-1/4" style={{backgroundColor: colors.accent}}></div>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-lg border" style={{backgroundColor: colors.secondaryLight, borderColor: colors.secondary}}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <X size={16} className="mr-2" style={{color: colors.secondary}} />
                          <div className="text-sm font-medium" style={{color: colors.secondary}}>Template Setup Failed</div>
                        </div>
                        <button className="text-xs px-2 py-1 rounded hover:opacity-80" style={{backgroundColor: colors.gray200, color: colors.secondary}}>
                          Retry
                        </button>
                      </div>
                      <div className="text-xs mt-2" style={{color: colors.secondary}}>Error: Signature field overlaps with existing text. Please reposition.</div>
                    </div>
                    
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <div className="text-sm font-medium mb-2">Recent Templates</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-2 bg-white rounded">
                          <div className="text-xs">Consulting_Agreement_Template</div>
                          <div className="text-xs" style={{color: colors.secondary}}>Setup incomplete</div>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white rounded">
                          <div className="text-xs">NDA_Template_v3</div>
                          <div className="text-xs" style={{color: colors.secondary}}>Fields missing</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content Side - 40% */}
            <motion.div 
              className="lg:col-span-2 order-2"
              variants={fadeInUpVariant}
            >
              <div className="space-y-8">
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <Settings size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Setup complexity</h3>
                    <p className="text-gray-600">Every document requires manual field placement and template configuration.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Trial pressure</h3>
                    <p className="text-gray-600">Free trials expire while still learning the platform basics.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <RefreshCw size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Endless tutorials</h3>
                    <p className="text-gray-600">Hours of video tutorials just to place signature fields correctly.</p>
                  </div>
                </motion.div>
              </div>
              
              <div className="mt-8 p-4 rounded-lg" style={{backgroundColor: colors.primaryLight}}>
                <div className="flex items-center">
                  <FileText size={20} style={{color: colors.accent}} className="mr-2" />
                  <div>
                    <div className="font-bold" style={{color: colors.accent}}>$200 + 4 hours lost</div>
                    <div className="text-sm" style={{color: colors.gray600}}>On platform setup and tutorials</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Panel 4: Approval Workflow Nightmare (40% content, 60% demo) */}
        <motion.div className="mb-20">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Content Side - 40% */}
            <motion.div 
              className="lg:col-span-2 order-2 lg:order-1"
              variants={fadeInUpVariant}
            >
              <div className="space-y-8">
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <Users size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Approval bottlenecks</h3>
                    <p className="text-gray-600">Multiple stakeholders create endless approval chains and delays.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Deal risk</h3>
                    <p className="text-gray-600">Competitors circle while contracts sit in approval limbo for weeks.</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="flex"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="mr-4 h-12 w-12 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                    <Clock size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: colors.gray900 }}>Signature chase</h3>
                    <p className="text-gray-600">Endless reminders and follow-ups to get simple signatures completed.</p>
                  </div>
                </motion.div>
              </div>
              
              <div className="mt-8 p-4 rounded-lg" style={{backgroundColor: colors.secondaryLight}}>
                <div className="flex items-center">
                  <Clock size={20} style={{color: colors.secondary}} className="mr-2" />
                  <div>
                    <div className="font-bold" style={{color: colors.secondary}}>$450 + 9 hours chasing</div>
                    <div className="text-sm" style={{color: colors.gray600}}>Signatures and approvals</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Demo Side - 60% */}
            <motion.div 
              className="lg:col-span-3 order-1 lg:order-2"
              variants={fadeInUpVariant}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.3 }
              }}
            >
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border" 
                   style={{
                     borderColor: 'rgba(226, 232, 240, 0.6)',
                     boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), 0 0 10px rgba(0, 0, 0, 0.02)'
                   }}>
                {/* Browser Header */}
                <div className="h-12 flex items-center px-5 border-b" 
                     style={{
                       background: 'linear-gradient(to right, #f9fafc, #f5f7fa)',
                       borderColor: 'rgba(226, 232, 240, 0.6)'
                     }}>
                  <div className="flex space-x-1.5 mr-5">
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-md px-3 py-1 mx-4">
                    <div className="text-xs" style={{color: colors.gray500}}>🔒 workflow.company.com</div>
                  </div>
                </div>
                
                {/* Workflow Interface */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{backgroundColor: colors.accent}}>
                        <Users size={16} color="white" />
                      </div>
                      <div className="text-sm font-medium">Contract Workflow</div>
                    </div>
                    <div className="text-xs px-2 py-1 rounded-full" style={{backgroundColor: colors.primaryLight, color: colors.accent}}>12 pending approvals</div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium">TechCorp Consulting Agreement</div>
                        <div className="text-xs text-gray-500">8 days in progress</div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <div className="flex items-center">
                            <Check size={16} className="mr-3" style={{color: colors.accent}} />
                            <div>
                              <div className="text-sm font-medium">Legal Team Review</div>
                              <div className="text-xs text-gray-500">Completed 6 days ago</div>
                            </div>
                          </div>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: colors.primaryLight}}>
                            <div className="text-xs font-bold" style={{color: colors.accent}}>JS</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 rounded border" style={{backgroundColor: colors.secondaryLight, borderColor: colors.secondary}}>
                          <div className="flex items-center">
                            <Clock size={16} className="mr-3" style={{color: colors.secondary}} />
                            <div>
                              <div className="text-sm font-medium">Finance Approval</div>
                              <div className="text-xs" style={{color: colors.secondary}}>Waiting 4 days - CFO out of office</div>
                            </div>
                          </div>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: colors.gray200}}>
                            <div className="text-xs font-bold" style={{color: colors.secondary}}>MK</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-gray-100 rounded border">
                          <div className="flex items-center">
                            <div className="w-4 h-4 rounded-full bg-gray-400 mr-3"></div>
                            <div>
                              <div className="text-sm font-medium text-gray-500">CEO Final Approval</div>
                              <div className="text-xs text-gray-400">Pending finance approval</div>
                            </div>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <div className="text-gray-500 text-xs font-bold">RD</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex items-center justify-between">
                        <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                          <div className="h-2 rounded-full w-1/3" style={{backgroundColor: colors.secondary}}></div>
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap">33% complete</div>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-lg border" style={{backgroundColor: colors.primaryLight, borderColor: colors.accent}}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <AlertTriangle size={16} className="mr-2" style={{color: colors.accent}} />
                          <div className="text-sm font-medium" style={{color: colors.accent}}>High Priority Alert</div>
                        </div>
                        <div className="text-xs" style={{color: colors.accent}}>2 hours ago</div>
                      </div>
                      <div className="text-sm" style={{color: colors.accent}}>Client mentioned competitor is also pitching. Deal at risk if not signed by Friday.</div>
                    </div>
                    
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <div className="text-sm font-medium mb-2">Other Pending Contracts</div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>DataFlow NDA</span>
                          <span style={{color: colors.secondary}}>Waiting 12 days</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span>CloudTech SOW</span>
                          <span style={{color: colors.accent}}>Waiting 18 days</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span>StartupX Partnership</span>
                          <span style={{color: colors.accent}}>Waiting 25 days</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Total Impact Summary - Matching LandingPage.jsx exactly */}
        <motion.div
          className="p-8 rounded-2xl border text-center mb-12 max-w-5xl mx-auto"
          style={{
            borderColor: colors.gray200,
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), 0 0 10px rgba(0, 0, 0, 0.02)'
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={panelsInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          whileHover={{ 
            y: -8,
            boxShadow: "0 20px 30px rgba(0, 0, 0, 0.08)",
            transition: { type: "spring", stiffness: 200, damping: 25 }
          }}
        >
          <h3 
            className="text-3xl font-bold mb-8" 
            style={{ ...fontStyles.heading, letterSpacing: '-0.02em' }}
          >
            The Hidden Cost of Contract Chaos
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8 text-center mb-8">
            <div>
              <div 
                className="text-4xl font-bold mb-2" 
                style={{ color: colors.accent, ...fontStyles.heading }}
              >
                ${animatedCost.toLocaleString()}
              </div>
              <div 
                className="text-base" 
                style={{ color: colors.gray700, ...fontStyles.body }}
              >
                Per Contract
              </div>
              <div 
                className="text-sm mt-1" 
                style={{ color: colors.gray500, ...fontStyles.body }}
              >
                Tools, legal fees, opportunity cost
              </div>
            </div>
            
            <div>
              <div 
                className="text-4xl font-bold mb-2" 
                style={{ color: colors.accent, ...fontStyles.heading }}
              >
                {Math.round(animatedTime)}h
              </div>
              <div 
                className="text-base" 
                style={{ color: colors.gray700, ...fontStyles.body }}
              >
                Team Time Lost
              </div>
              <div 
                className="text-sm mt-1" 
                style={{ color: colors.gray500, ...fontStyles.body }}
              >
                Across drafting, review, setup, chasing
              </div>
            </div>
            
            <div>
              <div 
                className="text-4xl font-bold mb-2" 
                style={{ color: colors.accent, ...fontStyles.heading }}
              >
                25
              </div>
              <div 
                className="text-base" 
                style={{ color: colors.gray700, ...fontStyles.body }}
              >
                Days Average
              </div>
              <div 
                className="text-sm mt-1" 
                style={{ color: colors.gray500, ...fontStyles.body }}
              >
                From draft to signature
              </div>
            </div>
          </div>

          <p 
            className="text-lg mb-8" 
            style={{ color: colors.gray700, lineHeight: '1.6', ...fontStyles.body }}
          >
            This doesn't include deals lost to frustrated prospects, competitive disadvantage,<br />
            or the stress of juggling multiple chaotic workflows simultaneously.
          </p>
        </motion.div>

        
      </div>
    </div>
  );
};

export default ProblemNetworkVisualization;